import React, { useEffect, useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { SquarePen } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/apiError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Modal } from "@/components/ui/modal";
import { EditDppForm } from "@/components/dpp/EditDppForm";
import { AddEventForm } from "@/components/dpp/AddEventForm";
import { TransferDppForm } from "@/components/dpp/TransferDppForm";
import { GuideModal } from "@/components/help/GuideModal";
import { GUIDE_ENABLED } from "@/lib/env";

interface Media {
  mediaType: string;
  type: string;
  url: string;
}

interface CustomAttr {
  type: string;
  value: string;
}

interface DppContent {
  name?: string;
  description?: string;
  medias?: Media[];
  customAttributes?: CustomAttr[];
  [key: string]: unknown;
}

interface DppOwner {
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  registered: boolean;
}

interface ArianeeDetail {
  content?: DppContent;
  tokenId?: string;
  minted?: boolean;
  customerEmail?: string | null;
  reference?: string | null;
  department?: string | null;
  billNumber?: string | null;
  owner?: DppOwner | null;
  [key: string]: unknown;
}

interface EventMedia {
  mediaType?: string;
  type?: string;
  url: string;
}

interface DppEvent {
  id: string;
  pending: boolean;
  timestamp: number;
  content: {
    type: string;
    title: string;
    description?: string;
    eventDate?: string;
    medias?: EventMedia[];
  };
}

function eventInstant(ev: DppEvent): number {
  const iso = ev.content.eventDate;
  const parsed = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(parsed) ? ev.timestamp * 1000 : parsed;
}

function fmtEventDate(ev: DppEvent): string {
  const d = new Date(eventInstant(ev));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ImageWithLoader({
  src,
  alt,
  style,
}: {
  src: string;
  alt: string;
  style?: React.CSSProperties;
}) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {!loaded && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, var(--surface-2) 25%, var(--surface-3) 50%, var(--surface-2) 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.4s infinite",
          }}
        />
      )}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        style={{
          ...style,
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.2s",
        }}
      />
    </div>
  );
}

function Lightbox({
  images,
  index,
  onClose,
  onNav,
}: {
  images: Media[];
  index: number;
  onClose: () => void;
  onNav: (i: number) => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav((index + 1) % images.length);
      if (e.key === "ArrowLeft")
        onNav((index - 1 + images.length) % images.length);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, onNav]);

  const img = images[index];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(8,9,18,0.92)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNav((index - 1 + images.length) % images.length);
          }}
          style={{
            position: "absolute",
            left: 24,
            background: "transparent",
            border: "1px solid var(--line)",
            color: "var(--ink-soft)",
            width: 40,
            height: 40,
            fontFamily: "var(--f-mono)",
            fontSize: 18,
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
          }}
        >
          ‹
        </button>
      )}

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "min(1000px, 90vw)",
          maxHeight: "88vh",
          position: "relative",
        }}
      >
        <img
          src={img.url}
          alt={`${index + 1} / ${images.length}`}
          style={{
            display: "block",
            maxWidth: "100%",
            maxHeight: "88vh",
            objectFit: "contain",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -28,
            left: 0,
            right: 0,
            textAlign: "center",
            fontFamily: "var(--f-mono)",
            fontSize: 11,
            letterSpacing: "0.14em",
            color: "var(--ink-mute)",
          }}
        >
          {index + 1} / {images.length}
        </div>
      </div>

      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNav((index + 1) % images.length);
          }}
          style={{
            position: "absolute",
            right: 24,
            background: "transparent",
            border: "1px solid var(--line)",
            color: "var(--ink-soft)",
            width: 40,
            height: 40,
            fontFamily: "var(--f-mono)",
            fontSize: 18,
            cursor: "pointer",
            display: "grid",
            placeItems: "center",
          }}
        >
          ›
        </button>
      )}

      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          background: "transparent",
          border: "1px solid var(--line)",
          color: "var(--ink-mute)",
          width: 36,
          height: 36,
          fontFamily: "var(--f-mono)",
          fontSize: 16,
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
        }}
      >
        ×
      </button>
    </div>
  );
}

export function DppPage() {
  const { brand, model, serial } = useSearch({ strict: false }) as {
    brand?: string;
    model?: string;
    serial?: string;
  };

  const { can } = useAuth();
  const [detail, setDetail] = useState<ArianeeDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [modal, setModal] = useState<null | "edit" | "event" | "transfer">(
    null
  );
  const [reloadFlag, setReloadFlag] = useState(0);
  const [events, setEvents] = useState<DppEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventLightbox, setEventLightbox] = useState<{
    images: Media[];
    index: number;
  } | null>(null);

  const hasMeta = brand && model && serial;

  useEffect(() => {
    if (!hasMeta) return;
    let cancelled = false;
    setLoading(true);
    setError("");

    api
      .get<ArianeeDetail>(
        `/admin/dpps/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/${encodeURIComponent(serial)}`
      )
      .then(({ data }) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(apiErrorMessage(err, "Failed to load passport detail"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [brand, model, serial, hasMeta, reloadFlag]);

  useEffect(() => {
    if (!hasMeta) return;
    let cancelled = false;
    setEventsLoading(true);

    api
      .get<{ events: DppEvent[] }>(
        `/admin/dpps/${encodeURIComponent(brand)}/${encodeURIComponent(model)}/${encodeURIComponent(serial)}/events`
      )
      .then(({ data }) => {
        if (!cancelled) setEvents(data.events ?? []);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [brand, model, serial, hasMeta, reloadFlag]);

  const content = detail?.content;
  const attrs = content?.customAttributes ?? [];
  const attrMap = Object.fromEntries(
    attrs.map((a) => [a.type.toLowerCase().replace(/\s+/g, "_"), a.value])
  );
  const images = (content?.medias ?? []).filter(
    (m) => m.mediaType === "picture"
  );

  const specFields: [string, string | undefined][] = [
    ["Brand", brand],
    ["Model", model],
    ["Serial", serial],
    ...(attrs.length > 0
      ? attrs
          .filter(
            (a) =>
              !["brand", "model", "serial number", "serial"].includes(
                a.type.toLowerCase()
              )
          )
          .map((a) => [a.type, a.value] as [string, string])
      : []),
    ...(content?.description
      ? [["Description", content.description] as [string, string]]
      : []),
  ];

  const watchLabel = [brand, model].filter(Boolean).join(" · ");
  const detailName = content?.name ?? watchLabel;

  return (
    <>
      <div className="adm__top">
        <div className="adm__crumbs">
          <Link to="/dpps">Passports</Link>
          <span className="sep">/</span>
          <span className="cur">
            {watchLabel} · {serial}
          </span>
        </div>
        <div className="row gap-12">
          {can("dpp:edit") && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setModal("edit")}
              title="Edit passport"
            >
              <SquarePen size={15} strokeWidth={2} />
              Edit
            </Button>
          )}
          {can("dpp:transfer") && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setModal("transfer")}
            >
              Transfer
            </Button>
          )}
          {can("dpp:event:add") && (
            <>
              {GUIDE_ENABLED && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setGuideOpen(true)}
                >
                  How events work
                </Button>
              )}
              <Button size="sm" onClick={() => setModal("event")}>
                + Add event
              </Button>
            </>
          )}
        </div>
      </div>

      {GUIDE_ENABLED && (
        <GuideModal
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
          topic="event"
        />
      )}

      <div className="adm__body">
        <div className="adm__page-head" style={{ marginBottom: 16 }}>
          <div className="adm__title-stack">
            <div className="meta">
              ↗ {brand?.toUpperCase()} · {serial}
            </div>
            <h1 className="h1" style={{ fontSize: 32, margin: 0 }}>
              {loading ? "—" : detailName}
            </h1>
          </div>
          <div className="row gap-12">
            {!loading && !error && <Badge variant="success">Verified</Badge>}
          </div>
        </div>

        {!hasMeta && (
          <div
            style={{
              padding: 32,
              border: "1px solid var(--line)",
              background: "var(--surface)",
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              color: "var(--ink-mute)",
              textAlign: "center",
            }}
          >
            No passport selected.{" "}
            <Link to="/dpps" className="alink">
              Back to list ↗
            </Link>
          </div>
        )}

        {hasMeta && (
          <>
            <div
              style={{
                border: "1px solid var(--line)",
                background: "var(--surface)",
                padding: 28,
                display: "grid",
                gridTemplateColumns: "200px 1fr",
                gap: 28,
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <div
                className="bracket-frame bracket-frame--sm"
                style={{
                  aspectRatio: "1",
                  background: "var(--surface-2)",
                  border: "1px solid var(--line)",
                  display: "grid",
                  placeItems: "center",
                  overflow: "hidden",
                }}
              >
                <span className="bk-bl" />
                <span className="bk-br" />
                {images[0] ? (
                  <div
                    onClick={() => setLightboxIndex(0)}
                    style={{ width: "100%", height: "100%", cursor: "zoom-in" }}
                  >
                    <ImageWithLoader
                      src={images[0].url}
                      alt={detailName}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      width: "64%",
                      aspectRatio: "1",
                      borderRadius: "50%",
                      border: "2px solid var(--ink-soft)",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: 2,
                        height: "30%",
                        background: "var(--ink)",
                        transformOrigin: "top center",
                        transform: "translate(-50%, -100%) rotate(-15deg)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: 2,
                        height: "22%",
                        background: "var(--ink)",
                        transformOrigin: "top center",
                        transform: "translate(-50%, -100%) rotate(95deg)",
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                {loading ? (
                  <div
                    style={{
                      fontFamily: "var(--f-mono)",
                      fontSize: 12,
                      color: "var(--ink-mute)",
                      letterSpacing: "0.12em",
                    }}
                  >
                    Loading detail…
                  </div>
                ) : error ? (
                  <div
                    style={{
                      fontFamily: "var(--f-mono)",
                      fontSize: 12,
                      color: "var(--danger)",
                    }}
                  >
                    {error}
                  </div>
                ) : (
                  <>
                    <h1
                      style={{
                        fontFamily: "var(--f-head)",
                        fontWeight: 500,
                        fontSize: 36,
                        lineHeight: 1,
                        margin: "8px 0 0",
                      }}
                    >
                      {detailName}
                    </h1>
                    {content?.description && (
                      <div
                        style={{
                          fontFamily: "var(--f-head)",
                          fontWeight: 400,
                          fontSize: 16,
                          color: "var(--ink-soft)",
                          margin: "4px 0 16px",
                          lineHeight: 1.4,
                        }}
                      >
                        {content.description}
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: 24,
                        flexWrap: "wrap",
                        marginTop: 16,
                        paddingTop: 16,
                        borderTop: "1px solid var(--line)",
                      }}
                    >
                      {[
                        ["BRAND", brand],
                        ["MODEL", model],
                        ["S/N", serial],
                        ...(attrMap["reference"]
                          ? [["REF", String(attrMap["reference"])]]
                          : []),
                        ...(attrMap["year"]
                          ? [["YEAR", String(attrMap["year"])]]
                          : []),
                        ...(attrMap["caliber"]
                          ? [["CAL", String(attrMap["caliber"])]]
                          : []),
                      ].map(([k, v]) => (
                        <div
                          key={k}
                          style={{ fontFamily: "var(--f-mono)", fontSize: 11 }}
                        >
                          <span
                            style={{
                              color: "var(--ink-mute)",
                              letterSpacing: "0.14em",
                              textTransform: "uppercase",
                            }}
                          >
                            {k}
                          </span>
                          <span style={{ color: "var(--ink)", marginLeft: 6 }}>
                            {v}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 340px",
                gap: 24,
              }}
            >
              <div>
                <Card>
                  <CardHeader>
                    <h3>Specification</h3>
                    {!loading && (
                      <span className="meta">{specFields.length} FIELDS</span>
                    )}
                  </CardHeader>
                  <div>
                    {loading ? (
                      <div
                        style={{
                          padding: 22,
                          fontFamily: "var(--f-mono)",
                          fontSize: 11,
                          color: "var(--ink-mute)",
                          letterSpacing: "0.12em",
                        }}
                      >
                        Loading…
                      </div>
                    ) : (
                      specFields.map(
                        ([k, v]) =>
                          v !== undefined && (
                            <div
                              key={k}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "160px 1fr",
                                gap: 16,
                                padding: "12px 22px",
                                borderBottom: "1px solid var(--line)",
                                alignItems: "baseline",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "var(--f-mono)",
                                  fontSize: 11,
                                  letterSpacing: "0.14em",
                                  textTransform: "uppercase",
                                  color: "var(--ink-mute)",
                                }}
                              >
                                {k}
                              </span>
                              <span
                                style={{ color: "var(--ink)", fontSize: 13 }}
                              >
                                {String(v)}
                              </span>
                            </div>
                          )
                      )
                    )}
                  </div>
                </Card>

                {images.length > 0 && (
                  <Card>
                    <CardHeader>
                      <h3>Images</h3>
                      <span className="meta">{images.length} PHOTOS</span>
                    </CardHeader>
                    <CardContent>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4, 1fr)",
                          gap: 8,
                        }}
                      >
                        {images.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => setLightboxIndex(i)}
                            style={{
                              display: "block",
                              aspectRatio: "1",
                              overflow: "hidden",
                              background: "var(--surface-2)",
                              border: "1px solid var(--line)",
                              cursor: "zoom-in",
                              padding: 0,
                            }}
                          >
                            <ImageWithLoader
                              src={img.url}
                              alt={`${detailName} · ${i + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader>
                    <h3>Provenance events</h3>
                    {!eventsLoading && (
                      <span className="meta">{events.length} EVENTS</span>
                    )}
                  </CardHeader>
                  <CardContent>
                    {eventsLoading ? (
                      <div
                        style={{
                          fontFamily: "var(--f-mono)",
                          fontSize: 11,
                          letterSpacing: "0.12em",
                          color: "var(--ink-mute)",
                        }}
                      >
                        Loading events…
                      </div>
                    ) : events.length === 0 ? (
                      <div
                        style={{
                          fontFamily: "var(--f-mono)",
                          fontSize: 11,
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "var(--ink-mute)",
                        }}
                      >
                        No events recorded
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {[...events]
                          .sort(
                            (a, b) =>
                              eventInstant(b) - eventInstant(a) ||
                              b.timestamp - a.timestamp ||
                              a.id.localeCompare(b.id)
                          )
                          .map((ev, idx, arr) => {
                            const evImages = (ev.content.medias ?? []).filter(
                              (m) => m.mediaType === "picture" || !m.mediaType
                            );
                            return (
                              <div
                                key={ev.id}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "16px 1fr",
                                  gap: 14,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 9,
                                      height: 9,
                                      marginTop: 5,
                                      borderRadius: "50%",
                                      background: ev.pending
                                        ? "var(--warning, #b8860b)"
                                        : "var(--accent)",
                                      flexShrink: 0,
                                    }}
                                  />
                                  {idx < arr.length - 1 && (
                                    <span
                                      style={{
                                        width: 1,
                                        flex: 1,
                                        background: "var(--line)",
                                        marginTop: 4,
                                      }}
                                    />
                                  )}
                                </div>

                                <div style={{ paddingBottom: 22 }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontFamily: "var(--f-mono)",
                                        fontSize: 10,
                                        letterSpacing: "0.14em",
                                        textTransform: "uppercase",
                                        color: "var(--accent)",
                                      }}
                                    >
                                      {ev.content.type}
                                    </span>
                                    <span
                                      style={{
                                        fontFamily: "var(--f-mono)",
                                        fontSize: 10.5,
                                        color: "var(--ink-mute)",
                                      }}
                                    >
                                      {fmtEventDate(ev)}
                                    </span>
                                    {ev.pending && (
                                      <Badge variant="warning">Pending</Badge>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      fontFamily: "var(--f-head)",
                                      fontWeight: 500,
                                      fontSize: 15,
                                      color: "var(--ink)",
                                      marginTop: 4,
                                    }}
                                  >
                                    {ev.content.title}
                                  </div>
                                  {ev.content.description && (
                                    <div
                                      style={{
                                        fontSize: 13,
                                        color: "var(--ink-soft)",
                                        marginTop: 4,
                                        lineHeight: 1.5,
                                      }}
                                    >
                                      {ev.content.description}
                                    </div>
                                  )}
                                  {evImages.length > 0 && (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: 8,
                                        flexWrap: "wrap",
                                        marginTop: 10,
                                      }}
                                    >
                                      {evImages.map((m, i) => (
                                        <button
                                          key={i}
                                          onClick={() =>
                                            setEventLightbox({
                                              images: evImages.map((x) => ({
                                                mediaType: "picture",
                                                type: x.type ?? "",
                                                url: x.url,
                                              })),
                                              index: i,
                                            })
                                          }
                                          style={{
                                            width: 56,
                                            height: 56,
                                            overflow: "hidden",
                                            border: "1px solid var(--line)",
                                            background: "var(--surface-2)",
                                            padding: 0,
                                            cursor: "zoom-in",
                                          }}
                                        >
                                          <ImageWithLoader
                                            src={m.url}
                                            alt={`${ev.content.title} · ${i + 1}`}
                                            style={{
                                              width: "100%",
                                              height: "100%",
                                              objectFit: "cover",
                                            }}
                                          />
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div style={{ display: "grid", gap: 16 }}>
                <Card>
                  <CardHeader>
                    <h3>Owner</h3>
                    <span className="meta">
                      {detail?.owner
                        ? detail.owner.registered
                          ? "REGISTERED"
                          : "GUEST"
                        : "DPP"}
                    </span>
                  </CardHeader>
                  {(() => {
                    const owner = detail?.owner ?? null;
                    const ownerName = owner
                      ? [owner.firstName, owner.lastName]
                          .filter(Boolean)
                          .join(" ")
                      : "";
                    const rows: [string, string, boolean][] = owner
                      ? [
                          ["Name", ownerName || "—", !!ownerName],
                          ["Email", owner.email, true],
                          ["Phone", owner.phone || "—", !!owner.phone],
                        ]
                      : [["Email", "Unassigned", false]];
                    return rows.map(([k, v, strong]) => (
                      <div
                        key={k}
                        style={{
                          padding: "16px 22px",
                          borderBottom: "1px solid var(--line)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          gap: 12,
                        }}
                      >
                        <span
                          style={{
                            fontFamily: "var(--f-mono)",
                            fontSize: 10,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: "var(--ink-mute)",
                          }}
                        >
                          {k}
                        </span>
                        <span
                          style={{
                            fontFamily: "var(--f-mono)",
                            fontSize: 13,
                            color: strong ? "var(--ink)" : "var(--ink-mute)",
                            letterSpacing: "0.04em",
                            wordBreak: "break-word",
                            textAlign: "right",
                          }}
                        >
                          {v}
                        </span>
                      </div>
                    ));
                  })()}
                </Card>

                <Card>
                  <CardHeader>
                    <h3>Arianee</h3>
                    <span className="meta">DPP</span>
                  </CardHeader>
                  {[
                    ["Brand", brand],
                    ["Model", model],
                    ["Serial", serial],
                    ...(detail?.billNumber
                      ? [["Bill", detail.billNumber]]
                      : []),
                    ...(detail?.tokenId ? [["Token ID", detail.tokenId]] : []),
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      style={{
                        padding: "16px 22px",
                        borderBottom: "1px solid var(--line)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                      }}
                    >
                      <span
                        style={{
                          fontFamily: "var(--f-mono)",
                          fontSize: 10,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          color: "var(--ink-mute)",
                        }}
                      >
                        {k}
                      </span>
                      <span
                        style={{
                          fontFamily: "var(--f-mono)",
                          fontSize: 13,
                          color: "var(--ink)",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </Card>

              </div>
            </div>
          </>
        )}
      </div>

      {lightboxIndex !== null && images.length > 0 && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNav={setLightboxIndex}
        />
      )}

      {eventLightbox && eventLightbox.images.length > 0 && (
        <Lightbox
          images={eventLightbox.images}
          index={eventLightbox.index}
          onClose={() => setEventLightbox(null)}
          onNav={(i) =>
            setEventLightbox((prev) => (prev ? { ...prev, index: i } : prev))
          }
        />
      )}

      {hasMeta && (
        <Modal
          open={modal === "edit"}
          title="Edit passport"
          onClose={() => setModal(null)}
        >
          <EditDppForm
            brand={brand}
            model={model}
            serial={serial}
            content={{
              name: content?.name,
              description: content?.description,
              medias: content?.medias,
            }}
            reference={
              detail?.reference ??
              (attrMap["reference"] ? String(attrMap["reference"]) : "")
            }
            department={detail?.department ?? ""}
            billNumber={detail?.billNumber ?? null}
            onSuccess={(saved) =>
              setDetail((prev) =>
                prev
                  ? {
                      ...prev,
                      content: saved.content as DppContent,
                      reference: saved.reference,
                      department: saved.department,
                    }
                  : prev
              )
            }
          />
        </Modal>
      )}

      {hasMeta && (
        <Modal
          open={modal === "event"}
          title="Add event"
          onClose={() => setModal(null)}
        >
          <AddEventForm
            brand={brand}
            model={model}
            serial={serial}
            onSuccess={() => setReloadFlag((n) => n + 1)}
          />
        </Modal>
      )}

      {hasMeta && (
        <Modal
          open={modal === "transfer"}
          title="Transfer ownership"
          onClose={() => setModal(null)}
        >
          <TransferDppForm
            brand={brand}
            model={model}
            serial={serial}
            onSuccess={() => setReloadFlag((n) => n + 1)}
          />
        </Modal>
      )}
    </>
  );
}
